import { useParams, useSearchParams, useLocation, useNavigate } from 'react-router-dom';
import { Ellie } from '../../components/Ellie';
import { useFeatureFlag } from '../../services/featureFlag.service';
import { FeatureFlag } from '../../services/featureFlag.service';
import FeatureFlagService from '../../services/featureFlag.service';
import { useState, useEffect } from 'react';
import { Loader2 } from 'lucide-react';
import { EllieHeader } from '../../components/EllieHeader';

export default function ConversationPage() {
  const { id } = useParams<{ id: string }>();
  const [searchParams] = useSearchParams();
  const location = useLocation();
  const navigate = useNavigate();
  const isAiAssistantEnabled = useFeatureFlag('ai-assistant', false);
  const useCasesEnabled = useFeatureFlag(FeatureFlag.ALLOW_USECASE_MANAGEMENT, false);
  const [flagsReady, setFlagsReady] = useState(() => FeatureFlagService.getInstance().isReady);

  // A question handed over from elsewhere — the home page's Ask Ellie box —
  // rides on history state rather than the URL, since it's a one-shot kickoff
  // and not part of the conversation's address. Captured once so it survives
  // the cleanup below.
  const [initialQuestion] = useState<string>(
    () => (location.state as { initialQuestion?: string } | null)?.initialQuestion || '',
  );

  // Strip it back off history state, so refreshing or navigating back into this
  // thread doesn't ask the same question a second time.
  useEffect(() => {
    if ((location.state as { initialQuestion?: string } | null)?.initialQuestion) {
      navigate(`${location.pathname}${location.search}`, { replace: true, state: null });
    }
  }, [location.pathname, location.search, location.state, navigate]);

  useEffect(() => {
    if (flagsReady) return;
    FeatureFlagService.getInstance().waitForInitialization().then(() => setFlagsReady(true));
  }, [flagsReady]);

  if (!flagsReady) {
    return (
      <div className="flex items-center justify-center h-64">
        <Loader2 className="h-8 w-8 animate-spin text-muted-foreground" />
      </div>
    );
  }

  if (!isAiAssistantEnabled) {
    return (
      <div className="flex items-center justify-center h-64">
        <Loader2 className="h-8 w-8 animate-spin text-muted-foreground" />
      </div>
    );
  }

  type AgentType = 'analyst' | 'use_case_discovery' | 'eda' | 'data_modeling' | 'ellie';
  const agentOverride = searchParams.get('agent') as AgentType | null;
  const agentType: AgentType = agentOverride || (useCasesEnabled ? 'ellie' : 'analyst');

  return (
    <div className="h-screen w-full flex flex-col">
      <EllieHeader />
      <div className="flex-1 min-h-0 overflow-hidden">
        {id && (
          <Ellie
            showHistory={true}
            conversationId={id}
            agentType={agentType}
            initialQuestion={initialQuestion}
          />
        )}
      </div>
    </div>
  );
}
